"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import InputPasswordWithReveal from "@/components/customs/InputPasswordWithReveal";
import { Link } from "react-router-dom";
import { SupportedLanguage } from "@/lib/shared";
import useRegister, { EmailFormValues } from "./useRegister";
import lang from "./lang.json";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import RenderCountries from "@/components/customs/RenderCountries";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { localizedPath } from "@/lib/localePath";

function FormRegisterWithEmail({ locale }: { locale: SupportedLanguage }) {
  const {
    form,
    onSubmit: originalOnSubmit,
    isLoading,
  } = useRegister({ tab: "email" });

  const typedForm = form as UseFormReturn<EmailFormValues>;

  const typedOnSubmit = originalOnSubmit as (
    values: EmailFormValues
  ) => Promise<void>;

  const handleSubmit: SubmitHandler<EmailFormValues> = (data) => {
    return typedOnSubmit(data);
  };

  return (
    <Form {...typedForm}>
      <form
        onSubmit={typedForm.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={typedForm.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["role"][locale]}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Personal" id="Personal" />
                      <Label htmlFor="Personal">
                        {lang["role-personal"][locale]}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Company" id="Company" />
                      <Label htmlFor="Company">
                        {lang["role-company"][locale]}
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={typedForm.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["fullname"][locale]}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={lang["placeholder-fullname"][locale]}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={typedForm.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["email"][locale]}
              </FormLabel>
              <FormControl>
                <Input placeholder="Ex: Mohamed@gmail.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <RenderCountries>
          {(countries) => {
            return (
              <div className="space-y-2 ">
                <FormField
                  control={typedForm.control}
                  name="countryShortName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm text-gray-700">
                        {lang["country"][locale]}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            size="default"
                            className="border-gray-200 focus:border-primary focus:ring-primary w-full"
                          >
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem
                                key={country.id}
                                value={country.shortName}
                              >
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            );
          }}
        </RenderCountries>

        <InputPasswordWithReveal
          form={typedForm}
          name="password"
          label={lang["password"][locale]}
          placeholder={lang["placeholder-password"][locale]}
        />

        <InputPasswordWithReveal
          form={typedForm}
          name="confirmPassword"
          label={lang["confirmPassword"][locale]}
          placeholder={lang["placeholder-confirmPassword"][locale]}
        />

        <FormField
          control={typedForm.control}
          name="accept"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-y-0">
              <FormControl>
                <Checkbox
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="flex items-center space-x-1">
                <label
                  htmlFor={field.name}
                  className={cn(
                    "text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer",
                    typedForm.formState.errors.accept && "text-destructive"
                  )}
                >
                  {lang["agree-to"][locale]}
                </label>
                <Link
                  to={localizedPath(locale, "/terms")}
                  className={cn(
                    "text-sm text-primary hover:underline",
                    typedForm.formState.errors.accept && "text-destructive"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {lang["terms-and-conditions"][locale]}
                </Link>
              </div>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          variant="default"
          className="w-full py-6 h-12 mt-6"
          disabled={isLoading}
        >
          {isLoading ? "Processing..." : lang["register"][locale]}
        </Button>
      </form>
    </Form>
  );
}

export default FormRegisterWithEmail;
